<template>
  <div :class="template">
    <b-overlay
      :show="!permission"
      rounded="lg"
      spinner-variant="customer"
      id="app"
      :style="cssProps"
    >
      <template v-if="$auth.check()">
        <!-- Modern Layout (Sidebar + TopBar) -->
        <div v-if="layoutMode === 'modern'" class="app-layout">
          <!-- Sidebar Navigation -->
          <SidebarNav :class="{ 'sidebar-open': sidebarOpen }" />

          <!-- Mobile Backdrop -->
          <div
            v-if="sidebarOpen"
            class="sidebar-backdrop"
            @click="sidebarOpen = false"
          ></div>

          <!-- Main Content Area -->
          <div class="main-area">
            <!-- Top Bar -->
            <TopBar
              @toggle-sidebar="sidebarOpen = !sidebarOpen"
              :layout-mode="layoutMode"
              :property-info="selectedPropertyInfo"
              @change-layout="setLayoutMode"
              @go-back="clearPropertySelection"
            />

            <!-- Page Content -->
            <main class="page-content">
              <router-view :layout-mode="layoutMode" />
            </main>
          </div>
        </div>

        <!-- Classic Layout (NavBar + Header + Footer) -->
        <div v-else class="bg-global main-app-wrapper">
          <NavBar :layout-mode="layoutMode" @change-layout="setLayoutMode" />

          <!-- Property Indicator Bar -->
          <transition name="slide-down">
            <div v-if="selectedPropertyInfo" class="property-indicator-bar">
              <div class="property-indicator-content">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 9.5L12 4L21 9.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M19 13V19.4C19 19.7314 18.7314 20 18.4 20H5.6C5.26863 20 5 19.7314 5 19.4V13"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span class="property-indicator-text">
                  <strong>{{ selectedPropertyInfo.title }}</strong>
                  <span class="property-indicator-separator">•</span>
                  {{ selectedPropertyInfo.project }}
                </span>
              </div>
            </div>
          </transition>

          <Header />
          <div class="classic-content-area">
            <div
              :class="[
                'content-wrapper',
                {
                  'full-width':
                    !rightSidebarData || rightSidebarData.length === 0,
                },
              ]"
            >
              <router-view :layout-mode="layoutMode" />
            </div>
            <transition name="sidebar-slide">
              <div
                class="right-sidebar-wrapper"
                v-if="rightSidebarData && rightSidebarData.length > 0"
              >
                <Sidebar :sidebar-data="rightSidebarData" />
              </div>
            </transition>
          </div>
          <Footer />
        </div>
      </template>
      <router-view v-else></router-view>
    </b-overlay>
  </div>
</template>

<script>
import SidebarNav from "./views/private/components/SidebarNav.vue";
import TopBar from "./views/private/components/TopBar.vue";
import NavBar from "./views/private/components/NavBar.vue";
import Header from "./views/private/components/Header.vue";
import Footer from "./views/private/components/Footer.vue";
import Sidebar from "./views/private/views/sidebar/Sidebar.vue";
export default {
  name: "App",
  data() {
    return {
      user: null,
      asset_id: null,
      apartments: [],
      tab_index: 0,
      selectedApartment: null,
      sidebarOpen: false,
      layoutMode: "classic", // 'modern' or 'classic'
      rightSidebarData: [],
      selectedPropertyInfo: null,
    };
  },
  components: { SidebarNav, TopBar, NavBar, Header, Footer, Sidebar },
  beforeCreate() {
    this.$store.commit("setCustomer", "abogados");
    this.$store.commit(
      "setSite",
      window.location.hostname.split(".").includes("tudespidoinjustificado")
        ? "tudespidoinjustificado"
        : "soydeudor"
    );
    const favicon = document.getElementById("favicon");
    favicon.href =
      this.$store.getters.getSite === "tudespidoinjustificado"
        ? "https://www.tudespidoinjustificado.cl/favicon.ico"
        : "https://www.soydeudor.com/favicon.ico";
  },
  mounted() {
    try {
      this.$store.commit("setCustomerData");
      if (this.$auth.check()) this.user = this.$auth.user();
      localStorage.setItem("layoutMode", "classic");
    } catch (e) {
      // ignore
    }

    // Listen for property selection from $root (for router-view compatibility)
    this.$root.$on("property-selected", this.updateSelectedProperty);
    // Listen for sidebar data changes from $root
    this.$root.$on("sidebar-data-changed", this.updateSidebarData);
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off("property-selected", this.updateSelectedProperty);
    this.$root.$off("sidebar-data-changed", this.updateSidebarData);
  },
  methods: {
    setLayoutMode() {
      this.layoutMode = "classic";
      try {
        localStorage.setItem("layoutMode", "classic");
      } catch (e) {
        // ignore
      }
    },
    updateSidebarData(data) {
      this.rightSidebarData = data || [];
    },
    updateSelectedProperty(propertyInfo) {
      this.selectedPropertyInfo = propertyInfo;
    },
    clearPropertySelection() {
      this.selectedPropertyInfo = null;
      // Emit event to child to go back to grid
      this.$root.$emit("go-back-to-grid");
    },
    logout() {
      this.$auth.logout({
        redirect: `/login`,
        success: function (msg) {
          console.error("LOGIN:", msg);
        },
      });
    },
  },
  computed: {
    cssProps() {
      return {
        "--color": `linear-gradient(90deg, ${this.color}, ${this.colorB})`,
        "--color-a": this.color,
        "--color-background": `linear-gradient(90deg, ${this.colorBackground}, ${this.colorBackgroundB})`,
        "--color-background-a": this.colorBackground,
        "--color-btn": `linear-gradient(90deg, ${this.colorBtn}, ${this.colorBtnB})`,
        "--color-btn-a": this.colorBtn,
        "--color-btn-text": this.colorBtnText,
        "--color-menu": `linear-gradient(90deg, ${this.colorMenu}, ${this.colorMenuB})`,
        "--color-menu-a": this.colorMenu,
        "--color-menu-text": this.colorMenuText,
        "--color-footer": `linear-gradient(90deg, ${this.colorFooter}, ${this.colorFooterB})`,
        "--color-footer-a": this.colorFooter,
        "--color-footer-text": this.colorFooterText,
        "--color-header": `linear-gradient(90deg, ${this.colorHeader}, ${this.colorHeaderB})`,
        "--color-header-a": this.colorHeader,
        "--color-text-header": this.colorTextHeader,
        "--color-var": `linear-gradient(90deg, ${this.colorVar}, ${this.colorVarB})`,
        "--color-var-text": this.colorVarText,
        "--color-var-a": this.colorVar,
        "--color-text": this.colorText,
        "--color-login-btn": `linear-gradient(90deg, ${this.colorLoginBtn}, ${this.colorLoginBtnB})`,
        "--color-login-btn-a": this.colorLoginBtn,
        "--color-login-btn-text": this.colorLoginBtnText,
      };
    },
    template() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.template
        : "default";
    },
    color() {
      return this.$store.getters.getSite === "tudespidoinjustificado"
        ? "#1a4a9f"
        : "#2A9D8F";
    },
    colorBackground() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_background
        : null;
    },
    colorBtn() {
      return this.$store.getters.getSite === "tudespidoinjustificado"
        ? "#1a4a9f"
        : "#2A9D8F";
    },
    colorBtnText() {
      return "#FFFFFF";
    },
    colorFooter() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_footer
        : null;
    },
    colorFooterText() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_footer_text
        : null;
    },
    colorLoginBtn() {
      return this.$store.getters.getSite === "tudespidoinjustificado"
        ? "#1a4a9f"
        : "#2A9D8F";
    },
    colorLoginBtnText() {
      return "#FFFFFF";
    },
    colorMenu() {
      return "#000000";
    },
    colorMenuText() {
      return "#FFFFFF";
    },
    colorText() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_text
        : null;
    },
    colorHeader() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_header
        : null;
    },
    colorTextHeader() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_text_header
        : null;
    },
    colorVar() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_var
        : null;
    },
    colorB() {
      return this.$store.getters.getSite === "tudespidoinjustificado"
        ? "#1a4a9f"
        : "#2A9D8F";
    },
    colorBackgroundB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_background_b
        : null;
    },
    colorBtnB() {
      return this.$store.getters.getSite === "tudespidoinjustificado"
        ? "#1a4a9f"
        : "#2A9D8F";
    },
    colorFooterB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_footer_b
        : null;
    },
    colorLoginBtnB() {
      return this.$store.getters.getSite === "tudespidoinjustificado"
        ? "#1a4a9f"
        : "#2A9D8F";
    },
    colorMenuB() {
      return "#000000";
    },
    colorHeaderB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_header_b
        : null;
    },
    colorVarB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_var_b
        : null;
    },
    colorVarText() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_var_text
        : null;
    },

    permission() {
      if (this.$store.getters.getCustomerData) {
        return this.$store.getters.getCustomerData.products.find(
          (product) => product.name == "Sala de Ventas Virtual"
        )
          ? true
          : false;
      } else {
        return false;
      }
    },
    customerId() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.customer_id
        : null;
    },
  },
};
</script>

<style>
/* ===== Modern App Layout ===== */
.app-layout {
  display: flex;
  min-height: 100vh;
  background: #f4f6f8;
}

.main-area {
  flex: 1;
  margin-left: 260px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  transition: margin-left 0.3s ease;
}

.page-content {
  flex: 1;
  padding: 24px;
}

/* Sidebar Backdrop for Mobile */
.sidebar-backdrop {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
}

/* ===== Classic App Layout ===== */
.main-app-wrapper {
  background-color: #f4f6f8;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.classic-content-area {
  display: flex;
  gap: 24px;
  width: 100%;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding: 24px 24px;
  box-sizing: border-box;
  align-items: flex-start;
}

.content-wrapper {
  flex: 0 0 70%;
  max-width: 70%;
  min-width: 0;
  padding: 35px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: flex 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.content-wrapper.full-width {
  flex: 0 0 100%;
  max-width: 100%;
}

.right-sidebar-wrapper {
  flex: 0 0 calc(30% - 24px); /* 30% minus the gap */
  max-width: calc(30% - 24px);
  position: sticky;
  top: 120px; /* navbar (70px) + property indicator bar (~40px) + spacing */
  align-self: flex-start;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
}

/* Sidebar slide transition */
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-slide-enter,
.sidebar-slide-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.sidebar-slide-enter-to,
.sidebar-slide-leave {
  opacity: 1;
  transform: translateX(0);
}

.right-sidebar-wrapper:empty {
  display: none;
}

/* Responsive Adjustments */
@media (max-width: 1024px) {
  .main-area {
    margin-left: 72px;
  }

  .classic-content-area {
    flex-direction: column;
    padding: 16px 16px;
    gap: 0;
  }

  .content-wrapper {
    flex: 0 0 100%;
    max-width: 100%;
    width: 100%;
  }

  .content-wrapper.full-width {
    flex: 0 0 100%;
    max-width: 100%;
    width: 100%;
  }

  .right-sidebar-wrapper {
    flex: 0 0 100%;
    max-width: 100%;
    width: 100%;
    position: static;
    margin-top: 16px;
  }
}

@media (max-width: 768px) {
  .classic-content-area {
    padding: 12px 12px;
  }

  .content-wrapper {
    padding: 16px;
  }

  .right-sidebar-wrapper {
    margin-top: 12px;
  }
}

@media (max-width: 576px) {
  .content-wrapper {
    border-radius: 20px;
  }
}

@media (max-width: 768px) {
  .main-area {
    margin-left: 0;
  }

  .sidebar-backdrop {
    display: block;
  }

  .page-content {
    padding: 16px;
  }
}

/* Property Indicator Bar */
.property-indicator-bar {
  background: var(--color-menu-text, #1f2937);
  color: var(--color-menu-a, #ffffff);
  padding: 10px 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 70px; /* Height of navbar */
  z-index: 999;
}

.property-indicator-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.property-indicator-content svg {
  flex-shrink: 0;
  opacity: 0.9;
}

.property-indicator-text {
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.property-indicator-text strong {
  font-weight: 600;
  text-transform: capitalize;
}

.property-indicator-separator {
  opacity: 0.6;
}

@media (max-width: 768px) {
  .property-indicator-bar {
    top: 64px; /* Mobile navbar height */
  }
}

@media (max-width: 576px) {
  .property-indicator-bar {
    padding: 8px 16px;
  }

  .property-indicator-text {
    font-size: 0.85rem;
  }
}

/* Property Indicator Bar Animation */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>

<style lang="scss">
@import "assets/sass/main";
.default {
  font-family: "Lato", sans-serif !important;
  @import "assets/sass/templates/default";
}
.cuadrado {
  font-family: "Montserrat", sans-serif !important;
  @import "assets/sass/templates/cuadrado";
}
.redondo {
  font-family: "Lato", sans-serif !important;
  @import "assets/sass/templates/redondo";
}
</style>
